import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sessionApi, authApi } from '../services/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { User, Shield, Clock, Monitor, MapPin, Key, Loader2, Eye, EyeOff } from 'lucide-react';

const AccountPage = () => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data } = await sessionApi.getActive();
        setSession(data.session);
      } catch (err) {
        console.error('Failed to fetch session:', err);
      } finally {
        setLoadingSession(false);
      }
    };

    fetchSession();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);

    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl" data-testid="account-page">
        <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-6">Account</h1>

        {/* User Info */}
        <div className="card-panel p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-airbus-blue/10 rounded-sm flex items-center justify-center">
              <User className="w-8 h-8 text-airbus-blue" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-cockpit-text-primary">{user?.email}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${
                  user?.role === 'founder' 
                    ? 'bg-status-warning/20 text-status-warning' 
                    : 'bg-cockpit-border text-cockpit-text-secondary'
                }`}>
                  {user?.role?.toUpperCase()}
                </span>
                {user?.is_primary_founder && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-red-900/30 text-red-300">
                    PRIMARY FOUNDER
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="label-text mb-1">Account Status</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${user?.is_verified ? 'bg-status-success' : 'bg-status-warning'}`} />
                <p className="text-sm text-cockpit-text-primary">
                  {user?.is_verified ? 'Verified' : 'Unverified'}
                </p>
              </div>
            </div>
            <div>
              <p className="label-text mb-1">Created</p>
              <p className="text-sm text-cockpit-text-primary">{formatDate(user?.created_at)}</p>
            </div>
            <div>
              <p className="label-text mb-1">Last Login</p>
              <p className="text-sm text-cockpit-text-primary">{formatDate(user?.last_login)}</p>
            </div>
          </div>
        </div>

        {/* Active Session */}
        <div className="card-panel p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-airbus-blue" />
            <h2 className="text-lg font-medium text-cockpit-text-primary">Active Session</h2>
          </div>

          {loadingSession ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 text-airbus-blue animate-spin" />
            </div>
          ) : session ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                <Monitor className="w-5 h-5 text-cockpit-text-secondary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-cockpit-text-primary truncate">{session.device_info}</p>
                  <p className="text-xs text-cockpit-text-secondary">Device</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                <MapPin className="w-5 h-5 text-cockpit-text-secondary" />
                <div>
                  <p className="text-sm text-cockpit-text-primary font-mono">{session.ip_address}</p>
                  <p className="text-xs text-cockpit-text-secondary">IP Address</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                <Clock className="w-5 h-5 text-cockpit-text-secondary" />
                <div>
                  <p className="text-sm text-cockpit-text-primary">{formatDate(session.created_at)}</p>
                  <p className="text-xs text-cockpit-text-secondary">Session Started</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-cockpit-text-secondary">No active session data available</p>
          )}
        </div>

        {/* Password Change */}
        <div className="card-panel p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-airbus-blue" />
              <h2 className="text-lg font-medium text-cockpit-text-primary">Password</h2>
            </div>
            {!showPasswordForm && (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="btn-secondary text-sm"
                data-testid="change-password-btn"
              >
                Change Password
              </button>
            )}
          </div>

          {passwordSuccess && (
            <div className="mb-4 p-3 bg-status-success/20 border border-status-success/50 rounded-sm">
              <p className="text-sm text-status-success">{passwordSuccess}</p>
            </div>
          )}

          {showPasswordForm && (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              {passwordError && (
                <div className="p-3 bg-red-900/30 border border-red-900 rounded-sm">
                  <p className="text-sm text-red-300">{passwordError}</p>
                </div>
              )}

              <div>
                <label className="label-text block mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="input-field pr-10"
                    required
                    data-testid="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-text-secondary hover:text-cockpit-text-primary"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="label-text block mb-2">New Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field"
                  required
                  data-testid="new-password"
                />
              </div>

              <div>
                <label className="label-text block mb-2">Confirm New Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  required
                  data-testid="confirm-new-password"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="submit-password-change"
                >
                  {passwordLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AccountPage;
